import { FormikProps } from 'formik';
import Button from '../../../components/common/Button';
import { Label } from '../../../components/common/Form';
import ImageUploader from '../../../components/common/Form/ImageUploader';
import { useCuisinesContext } from '../../../contexts/CuisinesContext';
import { MENU_LAYOUTS } from '../../../constants/MenuLayoutConstants';
import ConfirmationDisplayItem from '../../../components/ConfirmationDisplayItem';
import DayHourDisplay from '../../../components/DayHourDisplay';
import { buildRestaurantHours } from '../../../utils/time';
import { AlbumMediaInterface } from '../../../types/AlbumMediaInterface';
import { BlobWrapper } from '../../../types/MediaInterface';
import { RestaurantWizardFormValues } from './restaurantWizardSchema';
import { RestaurantImages } from './restaurantWizardTypes';

const SINGLE_IMAGES: { label: string; id: string; key: keyof Omit<RestaurantImages, 'galleryImages' | 'profileImages'> }[] = [
  { label: 'RESTAURANT LOGO', id: '0', key: 'logoImage' },
  { label: 'RESTAURANT SEARCH PHOTO', id: '1', key: 'thumbnailImage' },
  { label: 'MENU PHOTO', id: '3', key: 'menuCoverImage' }
];

const SOCIAL_LABELS: { label: string; key: keyof RestaurantWizardFormValues['socials'] }[] = [
  { label: 'INSTAGRAM', key: 'instagram' },
  { label: 'TIKTOK', key: 'tiktok' },
  { label: 'FACEBOOK', key: 'facebook' },
  { label: 'TWITTER', key: 'twitter' },
  { label: 'SNAPCHAT', key: 'snapchat' }
];

const getImageForGallery = (image: BlobWrapper | AlbumMediaInterface): Blob | string =>
  (image as BlobWrapper)?.file ?? (image as AlbumMediaInterface).mediaURL;

interface RestaurantConfirmationPageProps {
  formik: FormikProps<RestaurantWizardFormValues>;
  onBack: () => void;
  restaurantImages: RestaurantImages;
}

const RestaurantConfirmationPage = ({ formik, onBack, restaurantImages }: RestaurantConfirmationPageProps) => {
  const { getCuisine } = useCuisinesContext();
  const { galleryImages, profileImages } = restaurantImages;

  return (
    <div className="restaurant-confirmation-page">
      <ConfirmationDisplayItem label="NAME" value={formik.values?.name} />
      <ConfirmationDisplayItem label="PRIMARY TAGLINE" value={formik.values?.primaryTagline} />
      <ConfirmationDisplayItem label="SECONDARY TAGLINE" value={formik.values?.secondaryTagline} />
      <ConfirmationDisplayItem label="DESCRIPTION" value={formik.values?.description} />
      <ConfirmationDisplayItem label="PHONE" value={formik.values?.phone} />
      <ConfirmationDisplayItem label="EMAIL" value={formik.values?.email} />
      <ConfirmationDisplayItem label="CUISINE" value={getCuisine(formik.values?.cuisine)?.name} />
      <ConfirmationDisplayItem label="WEBSITE" value={formik.values?.website} />
      <div className="restaurant-confirmation-address-display">
        <div className="restaurant-confirmation-display-col-1">
          <ConfirmationDisplayItem label="STREET ADDRESS 1" value={formik.values?.address?.address1} />
          <ConfirmationDisplayItem label="STREET ADDRESS 2" value={formik.values?.address?.address2} />
        </div>
        <div className="restaurant-confirmation-display-col-2">
          <ConfirmationDisplayItem label="CITY" value={formik.values?.address?.city} />
          <ConfirmationDisplayItem label="STATE" value={formik.values?.address?.governingDistrict} />
          <ConfirmationDisplayItem label="ZIP CODE" value={formik.values?.address?.postCode} />
        </div>
      </div>
      <div className="restaurant-confirmation-socials-display">
        {SOCIAL_LABELS.map(({ label, key }) => (
          <ConfirmationDisplayItem key={key} label={label} value={formik.values?.socials?.[key]} />
        ))}
      </div>
      <DayHourDisplay label="HOURS" restaurantHours={buildRestaurantHours(formik.values?.restaurantHours)} />
      <ConfirmationDisplayItem label="NOTES" value={formik.values?.availabilityNotes} />
      {SINGLE_IMAGES.map(({ label, id, key }) => (
        <div key={id} className="restaurant-confirmation-img-display">
          <Label label={label} />
          <ImageUploader id={id} image={restaurantImages[key]} onUpdate={() => {}} readOnly />
        </div>
      ))}
      <div className="restaurant-confirmation-img-display">
        <Label label="RESTAURANT PROFILE PHOTOS" />
        <div className="restaurant-confirmation-gallery-display">
          {!profileImages?.length ? (
            <ImageUploader id="no-profile-image" image={null} onUpdate={() => {}} readOnly />
          ) : (
            profileImages.map((image, index) => (
              <ImageUploader
                key={image?.mediaID ?? `profile-${index}`}
                id={image?.mediaID?.toString() ?? `profile-${index}`}
                image={getImageForGallery(image)}
                onUpdate={() => {}}
                readOnly
              />
            ))
          )}
        </div>
      </div>
      <div className="restaurant-confirmation-img-display">
        <Label label="GALLERY PHOTOS" />
        <div className="restaurant-confirmation-gallery-display">
          {!galleryImages?.length ? (
            <ImageUploader id="no-image" image={null} onUpdate={() => {}} readOnly />
          ) : (
            galleryImages.map((image) => (
              <ImageUploader
                key={image?.mediaID}
                id={image?.mediaID?.toString()}
                image={getImageForGallery(image)}
                onUpdate={() => {}}
                readOnly
              />
            ))
          )}
        </div>
      </div>
      <ConfirmationDisplayItem label="MENU LAYOUT" value={MENU_LAYOUTS[formik.values.layoutID - 1]} />
      <div className="restaurant-wizard-confirm-buttons">
        <Button className="restaurant-wizard-back-button" onClick={onBack}>
          BACK
        </Button>
        <Button className="restaurant-wizard-next-button" submit isDisabled={!formik.isValid}>
          SUBMIT
        </Button>
      </div>
    </div>
  );
};

export default RestaurantConfirmationPage;
